import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';

import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import { ModernButton } from '../../components/ui/ModernButton';
import { Slider } from 'primereact/slider';
import { Toast } from 'primereact/toast';
import { Divider } from 'primereact/divider';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';

import { MultiSelect } from 'primereact/multiselect';
import { Chip } from 'primereact/chip';
import { useAuth } from '../../contexts/AuthContext';
import { SystemSettingsService, FeatureFlag } from '../../services/systemSettingsService';
import {
  getLimits,
  updateCalculatorLimits,
  updateInsightLimits,
  updateConnectionLimits
} from '../../services/limitsService';
import type {
  CalculatorLimitConfig,
  InsightLimitConfig,
  ConnectionTierLimits
} from '../../types/limits';
import { DangerZonePanel } from '../../components/admin/DangerZonePanel';

import './SystemSettingsPage.css';

type FeatureFlagCategory = 'all' | 'core_financial' | 'calculators' | 'insights' | 'scheduled_functions' | 'notifications' | 'authentication' | 'advanced' | 'refinance';

interface RoomData {
  name: string;
  shortName: string;
  type: string;
  area: number;
  length: number;
  width: number;
  cost: number;
  costSqFt: number;
}

interface StyleData {
  name: string;
  description: string;
}

interface CalculatorRow {
  id: string;
  displayName: string;
  enabled: boolean;
  monthlyLimit: number;
  requiresSubscription: boolean;
}

interface InsightRow {
  id: string;
  displayName: string;
  enabled: boolean;
  requiresSubscription: boolean;
}

interface MortgageRateData {
  currentRate: number;
  changeRate: number;
  fredSeriesKey: string;
  frequency: string;
  lastFiveObservations: number[];
  rateChanged: boolean;
  title: string;
  type?: string;
  lastUpdate?: any;
  rateDate?: any;
}

// Dynamic map of rate key to rate data
type MortgageRatesMap = Record<string, MortgageRateData>;

interface SystemSettings {
  company: {
    name: string;
    logo: string;
    primaryColor: string;
    secondaryColor: string;
    email: string;
    phone: string;
    address: string;
    website: string;
  };
  notifications: {
    emailEnabled: boolean;
    smsEnabled: boolean;
    pushEnabled: boolean;
    insightAlerts: boolean;
    customerActivityAlerts: boolean;
    systemAlerts: boolean;
    dailyDigest: boolean;
    weeklyReport: boolean;
  };
  insights: {
    autoGenerateInsights: boolean;
    insightRetentionDays: number;
    leadScoreThreshold: number;
    urgentPriorityThreshold: number;
    refreshIntervalHours: number;
  };
  integrations: {
    plaidEnabled: boolean;
    twilioEnabled: boolean;
    sendgridEnabled: boolean;
    webhookUrl: string;
  };
  staticData: {
    mortgageRates: MortgageRatesMap;
    dreamHome: {
      floors: string[];
      regions: string[];
      rooms: RoomData[];
      styles: StyleData[];
      pricePerSqFt: {
        standard: number;
        basement: number;
      };
      standardLotCost: number;
      standardLotSize: number;
    };
    globalSettings: {
      inflation: number;
      sandp_average: number;
      appreciationRate: string;
      seller_title_insurance_rate: string;
      subscriptionRenewalReminderDays: number;
    };
  };
}

// Fields owned by the Global Financial Settings section. Its auto-save writes
// only these fields so unsaved drafts in other sections are never persisted.
const GLOBAL_FINANCIAL_FIELDS: Array<keyof SystemSettings['staticData']['globalSettings']> = [
  'inflation',
  'sandp_average',
  'appreciationRate',
  'seller_title_insurance_rate',
  'subscriptionRenewalReminderDays'
];

export const SystemSettingsPage: React.FC = () => {
  const { currentAgent } = useAuth();
  const toast = useRef<Toast>(null);

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showRoomDialog, setShowRoomDialog] = useState(false);
  const [showStyleDialog, setShowStyleDialog] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<RoomData | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<StyleData | null>(null);
  const [featureFlags, setFeatureFlags] = useState<{ [key: string]: FeatureFlag }>({});
  const [loadingFlags, setLoadingFlags] = useState(false);
  const [flagsLoadAttempted, setFlagsLoadAttempted] = useState(false);
  const [flagsLoadFailed, setFlagsLoadFailed] = useState(false);
  const [updatingFlag, setUpdatingFlag] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<FeatureFlagCategory>('all');

  // Last persisted global financial settings, used to scope the section's auto-save
  const persistedGlobalSettingsRef = useRef<Record<string, any> | null>(null);

  // Subscription limits state
  const [limitsLoading, setLimitsLoading] = useState(false);
  const [limitsLoadAttempted, setLimitsLoadAttempted] = useState(false);
  const [limitsLoadFailed, setLimitsLoadFailed] = useState(false);
  const [limitsSaving, setLimitsSaving] = useState(false);
  const [limitsHasChanges, setLimitsHasChanges] = useState(false);
  const [calculatorRows, setCalculatorRows] = useState<CalculatorRow[]>([]);
  const [insightRows, setInsightRows] = useState<InsightRow[]>([]);
  const [defaultMonthlyLimit, setDefaultMonthlyLimit] = useState(5);
  const [freeLimits, setFreeLimits] = useState<ConnectionTierLimits>({
    maxPlaidInstitutions: 2,
    maxMortgageInstitutions: 1,
    maxAccountInstitutions: 1,
    maxMortgages: 1,
    maxBankAccounts: 3,
    transactionHistoryMonths: 3
  });
  const [subscriberLimits, setSubscriberLimits] = useState<ConnectionTierLimits>({
    maxPlaidInstitutions: 999,
    maxMortgageInstitutions: 999,
    maxAccountInstitutions: 999,
    maxMortgages: 999,
    maxBankAccounts: 999,
    transactionHistoryMonths: 24
  });

  const [settings, setSettings] = useState<SystemSettings>({
    company: {
      name: 'EquityWorkx',
      logo: '',
      primaryColor: '#3498db',
      secondaryColor: '#2ecc71',
      email: 'support@equityworkx.com',
      phone: '1-800-EQUITY-X',
      address: '123 Main St, Suite 100, City, ST 12345',
      website: 'https://equityworkx.com'
    },
    notifications: {
      emailEnabled: true,
      smsEnabled: false,
      pushEnabled: true,
      insightAlerts: true,
      customerActivityAlerts: true,
      systemAlerts: true,
      dailyDigest: false,
      weeklyReport: true
    },
    insights: {
      autoGenerateInsights: true,
      insightRetentionDays: 90,
      leadScoreThreshold: 70,
      urgentPriorityThreshold: 85,
      refreshIntervalHours: 24
    },
    integrations: {
      plaidEnabled: true,
      twilioEnabled: false,
      sendgridEnabled: true,
      webhookUrl: ''
    },
    staticData: {
      mortgageRates: {},
      dreamHome: {
        floors: ['Main Floor', 'Basement', '1st Floor', '2nd Floor', '3rd Floor'],
        regions: [
          'New England', 'Mid-Atlantic', 'East North Central',
          'West North Central', 'South Atlantic', 'East South Central',
          'West South Central', 'Mountain West', 'Pacific West'
        ],
        rooms: [],
        styles: [],
        pricePerSqFt: {
          standard: 185,
          basement: 45
        },
        standardLotCost: 400000,
        standardLotSize: 0.35
      },
      globalSettings: {
        inflation: 2.5,
        sandp_average: 10,
        appreciationRate: '3.5%',
        seller_title_insurance_rate: '0.047%',
        subscriptionRenewalReminderDays: 30
      }
    }
  });

	useEffect(() => {
		loadSettings();
	}, []);

	// Load feature flags once when the Feature Flags tab is activated.
	// Keyed off "attempted" (not off the data being empty) so a failed or
	// legitimately empty load never re-triggers the fetch.
	useEffect(() => {
		if (activeTab === 1 && !flagsLoadAttempted && !loadingFlags) {
			loadFeatureFlags();
		}
	}, [activeTab, flagsLoadAttempted, loadingFlags]);

	// Load subscription limits once when Subscription Limits tab is activated (tab index 2)
	useEffect(() => {
		if (activeTab === 2 && !limitsLoadAttempted && !limitsLoading) {
			loadSubscriptionLimits();
		}
	}, [activeTab, limitsLoadAttempted, limitsLoading]);

	const loadSettings = async () => {
		setLoading(true);
		try {
			const loadedSettings = await SystemSettingsService.getSettings();
			// Remember what is actually persisted so section-scoped saves can be
			// built without pulling in unsaved drafts from other sections
			persistedGlobalSettingsRef.current = { ...(loadedSettings.staticData.globalSettings || {}) };
			// Merge with current state to preserve any UI-specific fields
			setSettings(prev => ({
				...prev,
				...loadedSettings,
				// Ensure static data is properly merged with deep merge for nested objects
				staticData: {
					...prev.staticData,
					...loadedSettings.staticData,
					// Mortgage rates are now dynamic - just use what's in the database
					mortgageRates: loadedSettings.staticData.mortgageRates || {},
					dreamHome: {
						...prev.staticData.dreamHome,
						...(loadedSettings.staticData.dreamHome || {}),
						pricePerSqFt: {
							...prev.staticData.dreamHome.pricePerSqFt,
							...(loadedSettings.staticData.dreamHome?.pricePerSqFt || {})
						}
					},
					globalSettings: {
						...prev.staticData.globalSettings,
						...(loadedSettings.staticData.globalSettings || {})
					}
				}
			}));
		} catch (error) {
			console.error('Error loading settings:', error);
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to load settings',
				life: 3000
			});
		} finally {
			setLoading(false);
		}
	};

	const handleSaveSettings = async () => {
		setLoading(true);
		try {
			// Save to Firestore
			await SystemSettingsService.updateSettings(settings);
			persistedGlobalSettingsRef.current = { ...settings.staticData.globalSettings };

			toast.current?.show({
				severity: 'success',
				summary: 'Success',
				detail: 'Settings saved successfully',
				life: 3000
			});
		} catch (error) {
			console.error('Error saving settings:', error);
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to save settings',
				life: 3000
			});
		} finally {
			setLoading(false);
		}
	};

	const updateSettings = (category: keyof SystemSettings, field: string, value: any) => {
		setSettings(prev => ({
			...prev,
			[category]: {
				...prev[category],
				[field]: value
			}
		}));
	};

	const updateGlobalSettingsAndSave = async (field: string, value: any) => {
		const updatedGlobalSettings = {
			...settings.staticData.globalSettings,
			[field]: value
		};
		setSettings(prev => ({
			...prev,
			staticData: {
				...prev.staticData,
				globalSettings: {
					...prev.staticData.globalSettings,
					[field]: value
				}
			}
		}));

		// Persist only the fields this section owns. Start from the last persisted
		// values so unsaved drafts elsewhere on the page are not written, and any
		// stored field this page does not render is preserved.
		const globalSettingsPayload: Record<string, any> = {
			...(persistedGlobalSettingsRef.current || {})
		};
		GLOBAL_FINANCIAL_FIELDS.forEach(key => {
			globalSettingsPayload[key] = updatedGlobalSettings[key];
		});

		try {
			await SystemSettingsService.updateSettings({
				staticData: {
					globalSettings: globalSettingsPayload,
					// Owned by other sections - left undefined so those documents are untouched
					mortgageRates: undefined,
					dreamHome: undefined
				}
			});
			persistedGlobalSettingsRef.current = globalSettingsPayload;
			toast.current?.show({
				severity: 'success',
				summary: 'Saved',
				detail: 'Setting updated',
				life: 1500
			});
		} catch (error) {
			console.error('Error saving global setting:', error);
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to save setting',
				life: 3000
			});
		}
	};

	// Dream Home Functions
	const getRoomLabel = (room: RoomData) => room.shortName || room.name || 'this room template';

	const getStyleLabel = (style: StyleData) => style.name || 'this home style';

	const handleSaveRoom = () => {
		const missingFields: string[] = [];

		if (!selectedRoom?.name?.trim()) missingFields.push('Display Name');
		if (!selectedRoom?.shortName?.trim()) missingFields.push('Short Name');
		if (!selectedRoom?.type) missingFields.push('Type');
		if (!selectedRoom?.area || selectedRoom.area <= 0) missingFields.push('Area (sq ft)');
		if (!selectedRoom?.cost || selectedRoom.cost <= 0) missingFields.push('Total Cost');

		if (!selectedRoom || missingFields.length > 0) {
			toast.current?.show({
				severity: 'warn',
				summary: 'Missing Information',
				detail: `Please complete the following before saving: ${missingFields.join(', ')}`,
				life: 4000
			});
			return;
		}

		const roomToSave = selectedRoom;
		setSettings(prev => {
			const updatedRooms = [...prev.staticData.dreamHome.rooms];
			const existingIndex = updatedRooms.findIndex(r => r.shortName === roomToSave.shortName);

			if (existingIndex >= 0) {
				updatedRooms[existingIndex] = roomToSave;
			} else {
				updatedRooms.push(roomToSave);
			}

			return {
				...prev,
				staticData: {
					...prev.staticData,
					dreamHome: {
						...prev.staticData.dreamHome,
						rooms: updatedRooms
					}
				}
			};
		});
		setShowRoomDialog(false);
	};

	const handleDeleteRoom = (room: RoomData) => {
		confirmDialog({
			message: `Are you sure you want to delete the room template "${getRoomLabel(room)}"? This action cannot be undone.`,
			header: 'Confirm Delete',
			icon: 'pi pi-exclamation-triangle',
			acceptClassName: 'p-button-danger',
			accept: () => {
				setSettings(prev => ({
					...prev,
					staticData: {
						...prev.staticData,
						dreamHome: {
							...prev.staticData.dreamHome,
							rooms: prev.staticData.dreamHome.rooms.filter(r => r !== room)
						}
					}
				}));

				toast.current?.show({
					severity: 'success',
					summary: 'Removed',
					detail: `Room template "${getRoomLabel(room)}" removed. Use Save All Settings to apply.`,
					life: 3000
				});
			}
		});
	};

	const handleSaveStyle = () => {
		const missingFields: string[] = [];

		if (!selectedStyle?.name?.trim()) missingFields.push('Style Name');
		if (!selectedStyle?.description?.trim()) missingFields.push('Description');

		if (!selectedStyle || missingFields.length > 0) {
			toast.current?.show({
				severity: 'warn',
				summary: 'Missing Information',
				detail: `Please complete the following before saving: ${missingFields.join(', ')}`,
				life: 4000
			});
			return;
		}

		const styleToSave = selectedStyle;
		setSettings(prev => {
			const updatedStyles = [...prev.staticData.dreamHome.styles];
			const existingIndex = updatedStyles.findIndex(s => s.name === styleToSave.name);

			if (existingIndex >= 0) {
				updatedStyles[existingIndex] = styleToSave;
			} else {
				updatedStyles.push(styleToSave);
			}

			return {
				...prev,
				staticData: {
					...prev.staticData,
					dreamHome: {
						...prev.staticData.dreamHome,
						styles: updatedStyles
					}
				}
			};
		});
		setShowStyleDialog(false);
	};

	const handleDeleteStyle = (style: StyleData) => {
		confirmDialog({
			message: `Are you sure you want to delete the home style "${getStyleLabel(style)}"? This action cannot be undone.`,
			header: 'Confirm Delete',
			icon: 'pi pi-exclamation-triangle',
			acceptClassName: 'p-button-danger',
			accept: () => {
				setSettings(prev => ({
					...prev,
					staticData: {
						...prev.staticData,
						dreamHome: {
							...prev.staticData.dreamHome,
							styles: prev.staticData.dreamHome.styles.filter(s => s !== style)
						}
					}
				}));

				toast.current?.show({
					severity: 'success',
					summary: 'Removed',
					detail: `Home style "${getStyleLabel(style)}" removed. Use Save All Settings to apply.`,
					life: 3000
				});
			}
		});
	};

	// Feature Flags Functions
	const loadFeatureFlags = async () => {
		setLoadingFlags(true);
		setFlagsLoadAttempted(true);
		setFlagsLoadFailed(false);
		try {
			const flagsData = await SystemSettingsService.getFeatureFlags();
			setFeatureFlags(flagsData?.flags || {});
		} catch (error) {
			console.error('Error loading feature flags:', error);
			setFlagsLoadFailed(true);
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to load feature flags',
				life: 3000
			});
		} finally {
			setLoadingFlags(false);
		}
	};

	const toggleFeatureFlag = async (flagId: string) => {
		if (!currentAgent?.id) return;

		setUpdatingFlag(flagId);
		try {
			const currentFlag = featureFlags[flagId];
			if (!currentFlag) {
				throw new Error('Feature flag not found');
			}

			const newEnabledState = !currentFlag.isGloballyEnabled;
			const allRoles = ['super_admin', 'admin', 'master_agent', 'agent', 'user'];

			// When enabling a feature, automatically enable all roles
			// When disabling a feature, preserve existing role permissions
			const newRoles = newEnabledState ? allRoles : (currentFlag.enabledForRoles || []);

			await SystemSettingsService.updateFeatureFlag(
				flagId,
				{
					isGloballyEnabled: newEnabledState,
					enabledForRoles: newRoles
				},
				currentAgent.id
			);

			// Update local state
			setFeatureFlags(prev => {
				const updatedFlag = prev[flagId];
				if (!updatedFlag) return prev;

				return {
					...prev,
					[flagId]: {
						...updatedFlag,
						isGloballyEnabled: newEnabledState,
						enabledForRoles: newRoles,
						updatedAt: new Date().toISOString(),
						updatedBy: currentAgent.id
					}
				};
			});

			toast.current?.show({
				severity: 'success',
				summary: 'Success',
				detail: `Feature flag ${newEnabledState ? 'enabled' : 'disabled'}`,
				life: 2000
			});
		} catch (error) {
			console.error('Error updating feature flag:', error);
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to update feature flag',
				life: 3000
			});
		} finally {
			setUpdatingFlag(null);
		}
	};

	const toggleRoleAccess = async (flagId: string, role: string) => {
		if (!currentAgent?.id) return;

		setUpdatingFlag(flagId);
		try {
			const currentFlag = featureFlags[flagId];
			if (!currentFlag) {
				throw new Error('Feature flag not found');
			}

			const currentRoles = currentFlag.enabledForRoles || [];
			const newRoles = currentRoles.includes(role)
				? currentRoles.filter(r => r !== role)
				: [...currentRoles, role];

			await SystemSettingsService.updateFeatureFlag(
				flagId,
				{ enabledForRoles: newRoles },
				currentAgent.id
			);

			// Update local state
			setFeatureFlags(prev => {
				const updatedFlag = prev[flagId];
				if (!updatedFlag) return prev;

				return {
					...prev,
					[flagId]: {
						...updatedFlag,
						enabledForRoles: newRoles,
						updatedAt: new Date().toISOString(),
						updatedBy: currentAgent.id
					}
				};
			});

			toast.current?.show({
				severity: 'success',
				summary: 'Success',
				detail: `Role ${role} ${newRoles.includes(role) ? 'added' : 'removed'}`,
				life: 2000
			});
		} catch (error) {
			console.error('Error updating role access:', error);
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to update role access',
				life: 3000
			});
		} finally {
			setUpdatingFlag(null);
		}
	};

	const toggleAllRoles = async (flagId: string) => {
		if (!currentAgent?.id) return;

		setUpdatingFlag(flagId);
		try {
			const currentFlag = featureFlags[flagId];
			if (!currentFlag) {
				throw new Error('Feature flag not found');
			}

			const currentRoles = currentFlag.enabledForRoles || [];
			const allRoles = ['super_admin', 'admin', 'master_agent', 'agent', 'user'];

			// If all roles are enabled, disable all. Otherwise, enable all.
			const allEnabled = allRoles.every(role => currentRoles.includes(role));
			const newRoles = allEnabled ? [] : allRoles;

			await SystemSettingsService.updateFeatureFlag(
				flagId,
				{ enabledForRoles: newRoles },
				currentAgent.id
			);

			// Update local state
			setFeatureFlags(prev => {
				const updatedFlag = prev[flagId];
				if (!updatedFlag) return prev;

				return {
					...prev,
					[flagId]: {
						...updatedFlag,
						enabledForRoles: newRoles,
						updatedAt: new Date().toISOString(),
						updatedBy: currentAgent.id
					}
				};
			});

			toast.current?.show({
				severity: 'success',
				summary: 'Success',
				detail: `All roles ${allEnabled ? 'disabled' : 'enabled'}`,
				life: 2000
			});
		} catch (error) {
			console.error('Error updating all roles:', error);
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to update all roles',
				life: 3000
			});
		} finally {
			setUpdatingFlag(null);
		}
	};

	// Subscription Limits Functions
	const loadSubscriptionLimits = async () => {
		setLimitsLoading(true);
		setLimitsLoadAttempted(true);
		setLimitsLoadFailed(false);
		try {
			const data = await getLimits();

			// Transform calculator limits to rows
			const calcRows: CalculatorRow[] = Object.entries(data.calculatorLimits.calculators).map(
				([id, config]) => ({
					id,
					displayName: config.displayName,
					enabled: config.enabled,
					monthlyLimit: config.monthlyLimit,
					requiresSubscription: config.requiresSubscription
				})
			);

			// Transform insight limits to rows
			const insRows: InsightRow[] = Object.entries(data.insightLimits.insightTypes).map(
				([id, config]) => ({
					id,
					displayName: config.displayName,
					enabled: config.enabled,
					requiresSubscription: config.requiresSubscription
				})
			);

			setCalculatorRows(calcRows);
			setInsightRows(insRows);
			setDefaultMonthlyLimit(data.calculatorLimits.defaultMonthlyLimit);
			setFreeLimits(data.connectionLimits.free);
			setSubscriberLimits(data.connectionLimits.subscriber);
			setLimitsHasChanges(false);
		} catch (error) {
			console.error('Error loading subscription limits:', error);
			setLimitsLoadFailed(true);
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to load subscription limits',
				life: 3000
			});
		} finally {
			setLimitsLoading(false);
		}
	};

	const handleSaveLimits = async () => {
		setLimitsSaving(true);
		try {
			// Build calculator limits update
			const calculators: Record<string, Partial<CalculatorLimitConfig>> = {};
			calculatorRows.forEach(row => {
				calculators[row.id] = {
					enabled: row.enabled,
					monthlyLimit: row.monthlyLimit,
					requiresSubscription: row.requiresSubscription
				};
			});

			// Build insight limits update
			const insightTypes: Record<string, Partial<InsightLimitConfig>> = {};
			insightRows.forEach(row => {
				insightTypes[row.id] = {
					enabled: row.enabled,
					requiresSubscription: row.requiresSubscription
				};
			});

			// Save all three limit types
			await Promise.all([
				updateCalculatorLimits({ defaultMonthlyLimit, calculators }),
				updateInsightLimits({ insightTypes }),
				updateConnectionLimits({ free: freeLimits, subscriber: subscriberLimits })
			]);

			setLimitsHasChanges(false);
			toast.current?.show({
				severity: 'success',
				summary: 'Success',
				detail: 'Subscription limits saved successfully',
				life: 3000
			});
		} catch (error) {
			console.error('Error saving subscription limits:', error);
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to save subscription limits',
				life: 3000
			});
		} finally {
			setLimitsSaving(false);
		}
	};

	const updateCalculatorRow = (id: string, field: keyof CalculatorRow, value: any) => {
		setCalculatorRows(prev =>
			prev.map(row => (row.id === id ? { ...row, [field]: value } : row))
		);
		setLimitsHasChanges(true);
	};

	const updateInsightRow = (id: string, field: keyof InsightRow, value: any) => {
		setInsightRows(prev =>
			prev.map(row => (row.id === id ? { ...row, [field]: value } : row))
		);
		setLimitsHasChanges(true);
	};

	const updateFreeLimits = (field: keyof ConnectionTierLimits, value: number) => {
		setFreeLimits(prev => ({ ...prev, [field]: value }));
		setLimitsHasChanges(true);
	};

	const updateSubscriberLimits = (field: keyof ConnectionTierLimits, value: number) => {
		setSubscriberLimits(prev => ({ ...prev, [field]: value }));
		setLimitsHasChanges(true);
	};

	if (currentAgent?.role !== 'super_agent') {
		return (
			<div className="system-settings-page">
				<Card className="access-denied-card">
					<div className="access-denied-content">
						<i className="pi pi-lock" style={{ fontSize: '3rem', color: '#e74c3c' }}></i>
						<h3>Access Denied</h3>
						<p>Only Super Agents can access system settings.</p>
					</div>
				</Card>
			</div>
		);
	}

	return (
		<div className="system-settings-page">
			<Toast ref={toast} />
			<ConfirmDialog />

			<div className="page-header">
				<div className="header-content">
					<h1>System Settings</h1>
					<p>Configure authentication settings and mobile app data</p>
				</div>
				<ModernButton
					variant="primary"
					icon="pi pi-save"
					onClick={handleSaveSettings}
					loading={loading}
				>
					Save All Settings
				</ModernButton>
			</div>

			<TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
        <TabPanel header="Static Data">
          <Card className="settings-card">
            <h3>Mobile App Configuration</h3>
            <p className="section-description">
              Manage static data used by the mobile app. Changes here will be reflected in the app without requiring an update.
            </p>

            <Accordion>
              {/* Mortgage Rates Section */}
              <AccordionTab header="Mortgage Rates">
                <div className="mortgage-rates-section">
                  <p className="section-hint" style={{ marginBottom: '1.5rem', color: '#6c757d', fontSize: '0.9rem' }}>
                    These rates are automatically updated from FRED (Federal Reserve Economic Data). You can manually adjust the current rate if needed.
                  </p>
                  {Object.keys(settings.staticData.mortgageRates).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                      <i className="pi pi-info-circle" style={{ fontSize: '2rem', marginBottom: '1rem', display: 'block' }}></i>
                      <p>No mortgage rates found in the database.</p>
                    </div>
                  ) : (
                    Object.entries(settings.staticData.mortgageRates)
                      .sort(([keyA, dataA], [keyB, dataB]) => {
                        // Sort by type first (mortgage before prime/other)
                        const typeOrder: Record<string, number> = { mortgage: 0, prime: 1 };
                        const typeA = typeOrder[dataA.type || ''] ?? 2;
                        const typeB = typeOrder[dataB.type || ''] ?? 2;
                        if (typeA !== typeB) return typeA - typeB;

                        // Within same type, sort by term (extract number from key like "15Year", "30Year")
                        const termA = parseInt(keyA.match(/\d+/)?.[0] || '999');
                        const termB = parseInt(keyB.match(/\d+/)?.[0] || '999');
                        return termA - termB;
                      })
                      .map(([rateKey, rateData], index) => (
                      <React.Fragment key={rateKey}>
                        {index > 0 && <Divider />}
                        <div className="rate-card">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h4 style={{ margin: 0 }}>{rateData.title || rateKey}</h4>
                            {rateData.type && (
                              <Chip
                                label={rateData.type}
                                style={{
                                  backgroundColor: rateData.type === 'mortgage' ? '#e3f2fd' : '#fff3e0',
                                  color: rateData.type === 'mortgage' ? '#1565c0' : '#e65100',
                                  fontSize: '0.75rem',
                                  textTransform: 'uppercase'
                                }}
                              />
                            )}
                          </div>
                          <div className="settings-grid">
                            <div className="field">
                              <label htmlFor={`${rateKey}-rate`}>Current Rate (%)</label>
                              <InputNumber
                                id={`${rateKey}-rate`}
                                value={rateData.currentRate}
                                onValueChange={(e) => updateSettings('staticData', 'mortgageRates', {
                                  ...settings.staticData.mortgageRates,
                                  [rateKey]: { ...rateData, currentRate: e.value || 0 }
                                })}
                                mode="decimal"
                                minFractionDigits={2}
                                maxFractionDigits={2}
                                suffix="%"
                              />
                            </div>
                            <div className="field">
                              <label htmlFor={`${rateKey}-fred`}>FRED Series Key</label>
                              <InputText
                                id={`${rateKey}-fred`}
                                value={rateData.fredSeriesKey}
                                onChange={(e) => updateSettings('staticData', 'mortgageRates', {
                                  ...settings.staticData.mortgageRates,
                                  [rateKey]: { ...rateData, fredSeriesKey: e.target.value }
                                })}
                              />
                            </div>
                            <div className="field">
                              <label>Frequency</label>
                              <InputText
                                value={rateData.frequency || ''}
                                disabled
                                style={{ backgroundColor: '#f8f9fa' }}
                              />
                            </div>
                            <div className="field">
                              <label>Change Rate</label>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px'
                              }}>
                                <i
                                  className={`pi ${rateData.changeRate > 0 ? 'pi-arrow-up' : rateData.changeRate < 0 ? 'pi-arrow-down' : 'pi-minus'}`}
                                  style={{
                                    color: rateData.changeRate > 0 ? '#e74c3c' : rateData.changeRate < 0 ? '#27ae60' : '#6c757d'
                                  }}
                                ></i>
                                <span>{rateData.changeRate?.toFixed(2) || '0.00'}%</span>
                              </div>
                            </div>
                          </div>
                          <div className="recent-observations">
                            <label>Recent Observations</label>
                            <div className="observation-chips">
                              {(rateData.lastFiveObservations || []).map((rate: number, obsIndex: number) => (
                                <Chip key={obsIndex} label={`${rate}%`} />
                              ))}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    ))
                  )}
                </div>
              </AccordionTab>

              {/* Dream Home Configuration */}
              <AccordionTab header="Dream Home Calculator">
                <div className="dream-home-section">
                  {/* Regions */}
                  <div className="subsection">
                    <h4>Regions</h4>
                    <MultiSelect
                      value={settings.staticData.dreamHome.regions}
                      options={[
                        'New England', 'Mid-Atlantic', 'East North Central',
                        'West North Central', 'South Atlantic', 'East South Central',
                        'West South Central', 'Mountain West', 'Pacific West',
                        'Hawaii', 'Alaska'
                      ].map(r => ({ label: r, value: r }))}
                      onChange={(e) => updateSettings('staticData', 'dreamHome', {
                        ...settings.staticData.dreamHome,
                        regions: e.value
                      })}
                      display="comma"
                      placeholder="Select regions"
                      className="w-full"
                    />
                  </div>

                  <Divider />

                  {/* Floors */}
                  <div className="subsection">
                    <h4>Floor Options</h4>
                    <MultiSelect
                      value={settings.staticData.dreamHome.floors}
                      options={[
                        'Main Floor', 'Basement', '1st Floor', '2nd Floor',
                        '3rd Floor', '4th Floor', 'Attic', 'Sub-Basement'
                      ].map(f => ({ label: f, value: f }))}
                      onChange={(e) => updateSettings('staticData', 'dreamHome', {
                        ...settings.staticData.dreamHome,
                        floors: e.value
                      })}
                      display="comma"
                      placeholder="Select floor options"
                      className="w-full"
                    />
                  </div>

                  <Divider />

                  {/* Pricing */}
                  <div className="subsection">
                    <h4>Construction Pricing</h4>
                    <div className="settings-grid">
                      <div className="field">
                        <label htmlFor="standardPriceSqFt">Standard Price per Sq Ft</label>
                        <InputNumber
                          id="standardPriceSqFt"
                          value={settings.staticData.dreamHome.pricePerSqFt.standard}
                          onValueChange={(e) => updateSettings('staticData', 'dreamHome', {
                            ...settings.staticData.dreamHome,
                            pricePerSqFt: {
                              ...settings.staticData.dreamHome.pricePerSqFt,
                              standard: e.value || 0
                            }
                          })}
                          mode="currency"
                          currency="USD"
                        />
                      </div>
                      <div className="field">
                        <label htmlFor="basementPriceSqFt">Basement Price per Sq Ft</label>
                        <InputNumber
                          id="basementPriceSqFt"
                          value={settings.staticData.dreamHome.pricePerSqFt.basement}
                          onValueChange={(e) => updateSettings('staticData', 'dreamHome', {
                            ...settings.staticData.dreamHome,
                            pricePerSqFt: {
                              ...settings.staticData.dreamHome.pricePerSqFt,
                              basement: e.value || 0
                            }
                          })}
                          mode="currency"
                          currency="USD"
                        />
                      </div>
                      <div className="field">
                        <label htmlFor="standardLotCost">Standard Lot Cost</label>
                        <InputNumber
                          id="standardLotCost"
                          value={settings.staticData.dreamHome.standardLotCost}
                          onValueChange={(e) => updateSettings('staticData', 'dreamHome', {
                            ...settings.staticData.dreamHome,
                            standardLotCost: e.value || 0
                          })}
                          mode="currency"
                          currency="USD"
                        />
                      </div>
                      <div className="field">
                        <label htmlFor="standardLotSize">Standard Lot Size (acres)</label>
                        <InputNumber
                          id="standardLotSize"
                          value={settings.staticData.dreamHome.standardLotSize}
                          onValueChange={(e) => updateSettings('staticData', 'dreamHome', {
                            ...settings.staticData.dreamHome,
                            standardLotSize: e.value || 0
                          })}
                          mode="decimal"
                          minFractionDigits={2}
                          maxFractionDigits={2}
                        />
                      </div>
                    </div>
                  </div>

                  <Divider />

                  {/* Rooms */}
                  <div className="subsection">
                    <div className="subsection-header">
                      <h4>Room Templates</h4>
                      <ModernButton
                        variant="secondary"
                        size="sm"
                        icon="pi pi-plus"
                        onClick={() => {
                          setSelectedRoom(null);
                          setShowRoomDialog(true);
                        }}
                      >
                        Add Room
                      </ModernButton>
                    </div>
                    <DataTable
                      value={settings.staticData.dreamHome.rooms}
                      paginator
                      rows={10}
                      className="room-table"
                    >
                      <Column field="shortName" header="Room Name" />
                      <Column field="type" header="Type" />
                      <Column field="area" header="Area (sq ft)" />
                      <Column
                        field="costSqFt"
                        header="Cost/sq ft"
                        body={(rowData) => (
                          typeof rowData.costSqFt === 'number' ? `$${rowData.costSqFt.toLocaleString()}` : '-'
                        )}
                      />
                      <Column
                        field="cost"
                        header="Total Cost"
                        body={(rowData) => (
                          typeof rowData.cost === 'number' ? `$${rowData.cost.toLocaleString()}` : '-'
                        )}
                      />
                      <Column
                        body={(rowData) => (
                          <div className="action-buttons">
                            <Button
                              icon="pi pi-pencil"
                              className="p-button-text p-button-sm"
                              onClick={() => {
                                setSelectedRoom(rowData);
                                setShowRoomDialog(true);
                              }}
                            />
                            <Button
                              icon="pi pi-trash"
                              className="p-button-text p-button-danger p-button-sm"
                              onClick={() => handleDeleteRoom(rowData)}
                            />
                          </div>
                        )}
                      />
                    </DataTable>
                  </div>

                  <Divider />

                  {/* Styles */}
                  <div className="subsection">
                    <div className="subsection-header">
                      <h4>Home Styles</h4>
                      <ModernButton
                        variant="secondary"
                        size="sm"
                        icon="pi pi-plus"
                        onClick={() => {
                          setSelectedStyle(null);
                          setShowStyleDialog(true);
                        }}
                      >
                        Add Style
                      </ModernButton>
                    </div>
                    <DataTable
                      value={settings.staticData.dreamHome.styles}
                      paginator
                      rows={10}
                      className="style-table"
                    >
                      <Column field="name" header="Style Name" />
                      <Column
                        field="description"
                        header="Description"
                        style={{ maxWidth: '400px' }}
                        body={(rowData) => (
                          <div className="description-preview">
                            {rowData.description
                              ? `${rowData.description.substring(0, 100)}${rowData.description.length > 100 ? '...' : ''}`
                              : '-'}
                          </div>
                        )}
                      />
                      <Column
                        body={(rowData) => (
                          <div className="action-buttons">
                            <Button
                              icon="pi pi-pencil"
                              className="p-button-text p-button-sm"
                              onClick={() => {
                                setSelectedStyle(rowData);
                                setShowStyleDialog(true);
                              }}
                            />
                            <Button
                              icon="pi pi-trash"
                              className="p-button-text p-button-danger p-button-sm"
                              onClick={() => handleDeleteStyle(rowData)}
                            />
                          </div>
                        )}
                      />
                    </DataTable>
                  </div>
                </div>
              </AccordionTab>

              {/* Global Settings */}
              <AccordionTab header="Global Financial Settings">
                <div className="global-settings-section">
                  <div style={{ maxWidth: '400px', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div className="field">
                      <label htmlFor="inflationRate">Inflation Rate (%)</label>
                      <div className="slider-field">
                        <Slider
                          value={settings.staticData.globalSettings.inflation}
                          onChange={(e) => updateSettings('staticData', 'globalSettings', {
                            ...settings.staticData.globalSettings,
                            inflation: e.value as number
                          })}
                          onSlideEnd={(e) => updateGlobalSettingsAndSave('inflation', e.value as number)}
                          min={0}
                          max={10}
                          step={0.1}
                        />
                        <span className="slider-value">{settings.staticData.globalSettings.inflation}%</span>
                      </div>
                      <small>Annual inflation rate used in calculations</small>
                    </div>

                    <div className="field">
                      <label htmlFor="sandpAverage">S&P 500 Average Return (%)</label>
                      <div className="slider-field">
                        <Slider
                          value={settings.staticData.globalSettings.sandp_average}
                          onChange={(e) => updateSettings('staticData', 'globalSettings', {
                            ...settings.staticData.globalSettings,
                            sandp_average: e.value as number
                          })}
                          onSlideEnd={(e) => updateGlobalSettingsAndSave('sandp_average', e.value as number)}
                          min={0}
                          max={20}
                          step={0.5}
                        />
                        <span className="slider-value">{settings.staticData.globalSettings.sandp_average}%</span>
                      </div>
                      <small>Historical average annual return for S&P 500</small>
                    </div>

                    <div className="field">
                      <label htmlFor="appreciationRate">Home Appreciation Rate (%)</label>
                      <div className="slider-field">
                        <Slider
                          value={parseFloat(settings.staticData.globalSettings.appreciationRate?.replace('%', '') || '3.5')}
                          onChange={(e) => updateSettings('staticData', 'globalSettings', {
                            ...settings.staticData.globalSettings,
                            appreciationRate: `${e.value}%`
                          })}
                          onSlideEnd={(e) => updateGlobalSettingsAndSave('appreciationRate', `${e.value}%`)}
                          min={0}
                          max={15}
                          step={0.25}
                        />
                        <span className="slider-value">{settings.staticData.globalSettings.appreciationRate || '3.5%'}</span>
                      </div>
                      <small>Annual rate of appreciated value added to a mortgage's current estimated value</small>
                    </div>

                    <div className="field">
                      <label htmlFor="titleInsuranceRate">Seller Title Insurance Rate (%)</label>
                      <div className="slider-field">
                        <Slider
                          value={parseFloat(settings.staticData.globalSettings.seller_title_insurance_rate?.replace('%', '') || '0.047')}
                          onChange={(e) => updateSettings('staticData', 'globalSettings', {
                            ...settings.staticData.globalSettings,
                            seller_title_insurance_rate: `${e.value}%`
                          })}
                          onSlideEnd={(e) => updateGlobalSettingsAndSave('seller_title_insurance_rate', `${e.value}%`)}
                          min={0}
                          max={1}
                          step={0.001}
                        />
                        <span className="slider-value">{settings.staticData.globalSettings.seller_title_insurance_rate || '0.047%'}</span>
                      </div>
                      <small>Rate used for calculating seller's title insurance</small>
                    </div>

                    <div className="field">
                      <label htmlFor="subscriptionRenewalReminderDays">Subscription Renewal Reminder (Days)</label>
                      <div className="slider-field">
                        <Slider
                          value={settings.staticData.globalSettings.subscriptionRenewalReminderDays || 30}
                          onChange={(e) => updateSettings('staticData', 'globalSettings', {
                            ...settings.staticData.globalSettings,
                            subscriptionRenewalReminderDays: e.value as number
                          })}
                          onSlideEnd={(e) => updateGlobalSettingsAndSave('subscriptionRenewalReminderDays', e.value as number)}
                          min={1}
                          max={90}
                          step={1}
                        />
                        <span className="slider-value">{settings.staticData.globalSettings.subscriptionRenewalReminderDays || 30} days</span>
                      </div>
                      <small>Days before subscription renewal to send reminder notification</small>
                    </div>
                  </div>
                </div>
              </AccordionTab>
            </Accordion>
          </Card>
        </TabPanel>

        <TabPanel header="Feature Flags">
          <Card className="settings-card">
            <h3>Feature Flags Management</h3>
            <p className="section-description">
              Control feature availability and role-based access across the application.
            </p>

            {(loadingFlags || !flagsLoadAttempted) && Object.keys(featureFlags).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                <i className="pi pi-spinner pi-spin" style={{ fontSize: '3rem', marginBottom: '16px' }}></i>
                <p>Loading feature flags...</p>
              </div>
            ) : Object.keys(featureFlags).length > 0 ? (
              <>
                {/* Refresh Button */}
                <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                  <ModernButton
                    variant="secondary"
                    icon="pi pi-refresh"
                    onClick={loadFeatureFlags}
                    loading={loadingFlags}
                    size="sm"
                  >
                    Refresh Flags
                  </ModernButton>
                </div>

                {/* Category Filter */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                    Filter by Category
                  </label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {(['all', 'core_financial', 'calculators', 'insights', 'scheduled_functions', 'notifications', 'authentication', 'advanced', 'refinance'] as FeatureFlagCategory[]).map((category) => (
                      <Button
                        key={category}
                        label={category.replace('_', ' ').toUpperCase()}
                        className={selectedCategory === category ? 'p-button-sm' : 'p-button-sm p-button-outlined'}
                        onClick={() => setSelectedCategory(category)}
                        style={{
                          textTransform: 'capitalize',
                          fontSize: '12px'
                        }}
                      />
                    ))}
                  </div>
                </div>

                <Divider />

                {/* Feature Flags List */}
                <div className="feature-flags-list">
                  {Object.entries(featureFlags)
                    .filter(([_, flag]) => selectedCategory === 'all' || flag.category === selectedCategory)
                    .sort(([, a], [, b]) => a.name.localeCompare(b.name))
                    .map(([flagId, flag]) => (
                      <div key={flagId} className="feature-flag-item" style={{
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '16px',
                        backgroundColor: flag.isGloballyEnabled ? '#f8f9fa' : '#fff'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>{flag.name}</h4>
                              <Chip
                                label={flag.category.replace('_', ' ')}
                                style={{
                                  fontSize: '10px',
                                  textTransform: 'uppercase',
                                  backgroundColor: '#3498db',
                                  color: 'white'
                                }}
                              />
                            </div>
                            <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#6c757d' }}>
                              {flag.description}
                            </p>
                          </div>
                          <InputSwitch
                            checked={flag.isGloballyEnabled}
                            onChange={() => toggleFeatureFlag(flagId)}
                            disabled={updatingFlag === flagId}
                            tooltip={flag.isGloballyEnabled ? 'Disable feature' : 'Enable feature'}
                          />
                        </div>

                        {/* Role Permissions */}
                        {flag.isGloballyEnabled && (
                          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e9ecef' }}>
                            <label style={{ display: 'block', fontSize: '12px', color: '#6c757d', marginBottom: '8px' }}>
                              Enabled for roles:
                            </label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                              {/* All Roles Button */}
                              {(() => {
                                const allRoles = ['super_admin', 'admin', 'master_agent', 'agent', 'user'];
                                const allEnabled = allRoles.every(role => flag.enabledForRoles?.includes(role));
                                return (
                                  <Button
                                    label="All"
                                    className={allEnabled ? 'p-button-sm p-button-success' : 'p-button-sm p-button-outlined'}
                                    onClick={() => toggleAllRoles(flagId)}
                                    disabled={updatingFlag === flagId}
                                    style={{
                                      fontSize: '11px',
                                      fontWeight: 'bold',
                                      padding: '4px 12px',
                                      borderWidth: '2px'
                                    }}
                                  />
                                );
                              })()}

                              {/* Individual Role Buttons */}
                              {['super_admin', 'admin', 'master_agent', 'agent', 'user'].map((role) => {
                                const isEnabled = flag.enabledForRoles?.includes(role);
                                return (
                                  <Button
                                    key={role}
                                    label={role.replace('_', ' ')}
                                    className={isEnabled ? 'p-button-sm p-button-success' : 'p-button-sm p-button-outlined'}
                                    onClick={() => toggleRoleAccess(flagId, role)}
                                    disabled={updatingFlag === flagId}
                                    style={{
                                      fontSize: '11px',
                                      textTransform: 'capitalize',
                                      padding: '4px 10px'
                                    }}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>

                <Divider />

                {/* Summary */}
                <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#6c757d' }}>
                    <strong>Total:</strong> {Object.keys(featureFlags).length} flags
                    {' | '}
                    <strong>Enabled:</strong> {Object.values(featureFlags).filter((f: FeatureFlag) => f.isGloballyEnabled).length}
                    {' | '}
                    <strong>Disabled:</strong> {Object.values(featureFlags).filter((f: FeatureFlag) => !f.isGloballyEnabled).length}
                    {selectedCategory !== 'all' && (
                      <>
                        {' | '}
                        <strong>Filtered:</strong> {Object.values(featureFlags).filter((f: FeatureFlag) => f.category === selectedCategory).length} in {selectedCategory.replace('_', ' ')}
                      </>
                    )}
                  </p>
                </div>
              </>
            ) : flagsLoadFailed ? (
              <div className="load-state">
                <Message
                  severity="error"
                  text="Feature flags could not be loaded. This is usually a connection or permission problem."
                />
                <Button
                  label="Retry"
                  icon="pi pi-refresh"
                  className="p-button-sm"
                  onClick={loadFeatureFlags}
                  loading={loadingFlags}
                />
              </div>
            ) : (
              <div className="load-state">
                <Message severity="info" text="No feature flags are configured yet." />
                <Button
                  label="Reload"
                  icon="pi pi-refresh"
                  className="p-button-sm p-button-outlined"
                  onClick={loadFeatureFlags}
                  loading={loadingFlags}
                />
              </div>
            )}
          </Card>
        </TabPanel>

        <TabPanel header="Subscription Limits">
          <Card className="settings-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div>
                <h3 style={{ margin: 0 }}>Subscription Limits</h3>
                <p className="section-description" style={{ marginBottom: 0 }}>
                  Configure feature limits for free and subscriber tiers.
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {limitsHasChanges && (
                  <span style={{ color: '#f39c12', fontSize: '0.9rem' }}>
                    <i className="pi pi-exclamation-circle" style={{ marginRight: '0.5rem' }}></i>
                    Unsaved changes
                  </span>
                )}
                <ModernButton
                  variant="primary"
                  icon="pi pi-save"
                  onClick={handleSaveLimits}
                  loading={limitsSaving}
                  disabled={!limitsHasChanges}
                >
                  Save Limits
                </ModernButton>
              </div>
            </div>

            {limitsLoading || !limitsLoadAttempted ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <i className="pi pi-spinner pi-spin" style={{ fontSize: '3rem', color: '#3498db' }}></i>
                <p style={{ color: '#7f8c8d', marginTop: '1rem' }}>Loading subscription limits...</p>
              </div>
            ) : limitsLoadFailed ? (
              <div className="load-state">
                <Message
                  severity="error"
                  text="Subscription limits could not be loaded. This is usually a connection or permission problem."
                />
                <Button
                  label="Retry"
                  icon="pi pi-refresh"
                  className="p-button-sm"
                  onClick={loadSubscriptionLimits}
                  loading={limitsLoading}
                />
              </div>
            ) : (
              <Accordion multiple activeIndex={[0, 1, 2]}>
                {/* Connection Limits */}
                <AccordionTab header="Connection Limits">
                  <div style={{ background: '#f0f7ff', border: '1px solid #cce5ff', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                      <i className="pi pi-info-circle" style={{ color: '#0066cc', fontSize: '1.25rem', marginTop: '2px' }}></i>
                      <div>
                        <strong style={{ color: '#004085' }}>Connection Limits</strong>
                        <p style={{ margin: '0.5rem 0 0 0', color: '#004085', fontSize: '0.9rem', lineHeight: '1.5' }}>
                          Control how many financial accounts users can connect based on their subscription status.
                          Free users have restricted limits while subscribers get expanded or unlimited access.
                          Use 999 for effectively unlimited connections.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                    {/* Free Tier */}
                    <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '1.5rem' }}>
                      <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                        <i className="pi pi-user" style={{ color: '#6c757d' }}></i>
                        Free Tier
                      </h4>
                      <div className="field" style={{ marginBottom: '1rem' }}>
                        <label>Max Plaid Institutions</label>
                        <InputNumber
                          value={freeLimits.maxPlaidInstitutions}
                          onValueChange={(e) => updateFreeLimits('maxPlaidInstitutions', e.value || 0)}
                          min={0}
                          max={999}
                          style={{ width: '100%' }}
                        />
                        <small style={{ color: '#7f8c8d' }}>Total linked financial institutions</small>
                      </div>
                      <div className="field" style={{ marginBottom: '1rem' }}>
                        <label>Max Mortgage Institutions</label>
                        <InputNumber
                          value={freeLimits.maxMortgageInstitutions}
                          onValueChange={(e) => updateFreeLimits('maxMortgageInstitutions', e.value || 0)}
                          min={0}
                          max={999}
                          style={{ width: '100%' }}
                        />
                        <small style={{ color: '#7f8c8d' }}>Mortgage-type institutions allowed</small>
                      </div>
                      <div className="field" style={{ marginBottom: '1rem' }}>
                        <label>Max Account Institutions</label>
                        <InputNumber
                          value={freeLimits.maxAccountInstitutions}
                          onValueChange={(e) => updateFreeLimits('maxAccountInstitutions', e.value || 0)}
                          min={0}
                          max={999}
                          style={{ width: '100%' }}
                        />
                        <small style={{ color: '#7f8c8d' }}>Bank account institutions allowed</small>
                      </div>
                      <div className="field" style={{ marginBottom: '1rem' }}>
                        <label>Max Mortgages</label>
                        <InputNumber
                          value={freeLimits.maxMortgages}
                          onValueChange={(e) => updateFreeLimits('maxMortgages', e.value || 0)}
                          min={0}
                          max={999}
                          style={{ width: '100%' }}
                        />
                        <small style={{ color: '#7f8c8d' }}>Total mortgage accounts</small>
                      </div>
                      <div className="field" style={{ marginBottom: '1rem' }}>
                        <label>Max Bank Accounts</label>
                        <InputNumber
                          value={freeLimits.maxBankAccounts}
                          onValueChange={(e) => updateFreeLimits('maxBankAccounts', e.value || 0)}
                          min={0}
                          max={999}
                          style={{ width: '100%' }}
                        />
                        <small style={{ color: '#7f8c8d' }}>Total bank accounts</small>
                      </div>
                      <div className="field">
                        <label>Transaction History (Months)</label>
                        <InputNumber
                          value={freeLimits.transactionHistoryMonths}
                          onValueChange={(e) => updateFreeLimits('transactionHistoryMonths', e.value || 0)}
                          min={1}
                          max={120}
                          style={{ width: '100%' }}
                        />
                        <small style={{ color: '#7f8c8d' }}>Months of transaction history</small>
                      </div>
                    </div>

                    {/* Subscriber Tier */}
                    <div style={{ background: '#fff8e6', borderRadius: '8px', padding: '1.5rem' }}>
                      <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                        <i className="pi pi-star" style={{ color: '#f39c12' }}></i>
                        Subscriber Tier
                      </h4>
                      <div className="field" style={{ marginBottom: '1rem' }}>
                        <label>Max Plaid Institutions</label>
                        <InputNumber
                          value={subscriberLimits.maxPlaidInstitutions}
                          onValueChange={(e) => updateSubscriberLimits('maxPlaidInstitutions', e.value || 0)}
                          min={0}
                          max={999}
                          style={{ width: '100%' }}
                        />
                        <small style={{ color: '#7f8c8d' }}>Use 999 for unlimited</small>
                      </div>
                      <div className="field" style={{ marginBottom: '1rem' }}>
                        <label>Max Mortgage Institutions</label>
                        <InputNumber
                          value={subscriberLimits.maxMortgageInstitutions}
                          onValueChange={(e) => updateSubscriberLimits('maxMortgageInstitutions', e.value || 0)}
                          min={0}
                          max={999}
                          style={{ width: '100%' }}
                        />
                        <small style={{ color: '#7f8c8d' }}>Use 999 for unlimited</small>
                      </div>
                      <div className="field" style={{ marginBottom: '1rem' }}>
                        <label>Max Account Institutions</label>
                        <InputNumber
                          value={subscriberLimits.maxAccountInstitutions}
                          onValueChange={(e) => updateSubscriberLimits('maxAccountInstitutions', e.value || 0)}
                          min={0}
                          max={999}
                          style={{ width: '100%' }}
                        />
                        <small style={{ color: '#7f8c8d' }}>Use 999 for unlimited</small>
                      </div>
                      <div className="field" style={{ marginBottom: '1rem' }}>
                        <label>Max Mortgages</label>
                        <InputNumber
                          value={subscriberLimits.maxMortgages}
                          onValueChange={(e) => updateSubscriberLimits('maxMortgages', e.value || 0)}
                          min={0}
                          max={999}
                          style={{ width: '100%' }}
                        />
                        <small style={{ color: '#7f8c8d' }}>Use 999 for unlimited</small>
                      </div>
                      <div className="field" style={{ marginBottom: '1rem' }}>
                        <label>Max Bank Accounts</label>
                        <InputNumber
                          value={subscriberLimits.maxBankAccounts}
                          onValueChange={(e) => updateSubscriberLimits('maxBankAccounts', e.value || 0)}
                          min={0}
                          max={999}
                          style={{ width: '100%' }}
                        />
                        <small style={{ color: '#7f8c8d' }}>Use 999 for unlimited</small>
                      </div>
                      <div className="field">
                        <label>Transaction History (Months)</label>
                        <InputNumber
                          value={subscriberLimits.transactionHistoryMonths}
                          onValueChange={(e) => updateSubscriberLimits('transactionHistoryMonths', e.value || 0)}
                          min={1}
                          max={120}
                          style={{ width: '100%' }}
                        />
                        <small style={{ color: '#7f8c8d' }}>Months of transaction history</small>
                      </div>
                    </div>
                  </div>
                </AccordionTab>

                {/* Calculator Limits */}
                <AccordionTab header="Calculator Limits">
                  <div style={{ background: '#f0f7ff', border: '1px solid #cce5ff', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                      <i className="pi pi-info-circle" style={{ color: '#0066cc', fontSize: '1.25rem', marginTop: '2px' }}></i>
                      <div>
                        <strong style={{ color: '#004085' }}>Calculator Access Settings</strong>
                        <p style={{ margin: '0.5rem 0 0 0', color: '#004085', fontSize: '0.9rem', lineHeight: '1.5' }}>
                          <strong>Enabled:</strong> When ON, free users have a monthly usage limit. When OFF, free users have unlimited access (no tracking).<br/>
                          <strong>Monthly Limit:</strong> Maximum uses per month for free users. Resets on the 1st of each month. Only applies when Enabled is ON.<br/>
                          <strong>Subscription Only:</strong> When ON, the calculator is completely locked for non-subscribers (they see an upgrade prompt). Takes precedence over other settings.
                        </p>
                        <p style={{ margin: '0.5rem 0 0 0', color: '#004085', fontSize: '0.85rem', fontStyle: 'italic' }}>
                          Note: Subscribers always have unlimited access to all calculators regardless of these settings.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: '#e3f2fd', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                    <label style={{ fontWeight: 600, color: '#1565c0', whiteSpace: 'nowrap' }}>
                      Default Monthly Limit:
                    </label>
                    <InputNumber
                      value={defaultMonthlyLimit}
                      onValueChange={(e) => {
                        setDefaultMonthlyLimit(e.value || 5);
                        setLimitsHasChanges(true);
                      }}
                      min={1}
                      max={999}
                      style={{ width: '80px' }}
                    />
                    <span style={{ color: '#1976d2', fontSize: '0.875rem' }}>Uses per month for free users (applies to new calculators)</span>
                  </div>
                  <DataTable value={calculatorRows} className="p-datatable-sm" tableStyle={{ tableLayout: 'fixed', width: '100%' }}>
                    <Column field="displayName" header="Calculator" sortable style={{ width: '35%' }} />
                    <Column
                      field="enabled"
                      header="Free User Limits Enabled"
                      body={(rowData: CalculatorRow) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <InputSwitch
                            checked={rowData.enabled}
                            onChange={(e) => updateCalculatorRow(rowData.id, 'enabled', e.value)}
                          />
                          <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                            {rowData.enabled ? 'On' : 'Off'}
                          </span>
                        </div>
                      )}
                    />
                    <Column
                      field="monthlyLimit"
                      header="Monthly Limit"
                      body={(rowData: CalculatorRow) => (
                        <InputNumber
                          value={rowData.monthlyLimit}
                          onValueChange={(e) => updateCalculatorRow(rowData.id, 'monthlyLimit', e.value || 0)}
                          min={1}
                          max={999}
                          style={{ width: '80px !important' }}
                          disabled={!rowData.enabled}
                        />
                      )}
                    />
                    <Column
                      field="requiresSubscription"
                      header="Subscription Only"
                      body={(rowData: CalculatorRow) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <InputSwitch
                            checked={rowData.requiresSubscription}
                            onChange={(e) => updateCalculatorRow(rowData.id, 'requiresSubscription', e.value)}
                            disabled={!rowData.enabled}
                          />
                          <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                            {rowData.requiresSubscription ? 'Yes' : 'No'}
                          </span>
                        </div>
                      )}
                    />
                  </DataTable>
                </AccordionTab>

                {/* Insight Limits */}
                <AccordionTab header="Insight Limits">
                  <div style={{ background: '#f0f7ff', border: '1px solid #cce5ff', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                      <i className="pi pi-info-circle" style={{ color: '#0066cc', fontSize: '1.25rem', marginTop: '2px' }}></i>
                      <div>
                        <strong style={{ color: '#004085' }}>Insight Access Settings</strong>
                        <p style={{ margin: '0.5rem 0 0 0', color: '#004085', fontSize: '0.9rem', lineHeight: '1.5' }}>
                          <strong>Enabled:</strong> When ON, the insight type is available. When OFF, the insight type is disabled for free users.<br/>
                          <strong>Subscription Only:</strong> When ON, the insight content is blurred/locked for non-subscribers. They can see the insight exists but must subscribe to view details.
                        </p>
                        <p style={{ margin: '0.5rem 0 0 0', color: '#004085', fontSize: '0.85rem', fontStyle: 'italic' }}>
                          Note: Insights are still generated for all users. This setting only controls whether free users can view the content. Subscribers always have full access.
                        </p>
                      </div>
                    </div>
                  </div>
                  <DataTable value={insightRows} className="p-datatable-sm" tableStyle={{ tableLayout: 'fixed', width: '100%' }}>
                    <Column field="displayName" header="Insight Type" sortable style={{ width: '50%' }} />
                    <Column
                      field="enabled"
                      header="Enabled"
                      style={{ width: '25%' }}
                      body={(rowData: InsightRow) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <InputSwitch
                            checked={rowData.enabled}
                            onChange={(e) => updateInsightRow(rowData.id, 'enabled', e.value)}
                          />
                          <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                            {rowData.enabled ? 'On' : 'Off'}
                          </span>
                        </div>
                      )}
                    />
                    <Column
                      field="requiresSubscription"
                      header="Subscription Only"
                      style={{ width: '25%' }}
                      body={(rowData: InsightRow) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <InputSwitch
                            checked={rowData.requiresSubscription}
                            onChange={(e) => updateInsightRow(rowData.id, 'requiresSubscription', e.value)}
                            disabled={!rowData.enabled}
                          />
                          <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                            {rowData.requiresSubscription ? 'Yes' : 'No'}
                          </span>
                        </div>
                      )}
                    />
                  </DataTable>
                </AccordionTab>
              </Accordion>
            )}
          </Card>
        </TabPanel>

        {/* Danger Zone Tab - Super Agent Only */}
        <TabPanel header="Danger Zone">
          <DangerZonePanel toast={toast} />
        </TabPanel>
      </TabView>

      {/* Room Dialog */}
      <Dialog
        visible={showRoomDialog}
        onHide={() => setShowRoomDialog(false)}
        header={selectedRoom ? "Edit Room Template" : "Add Room Template"}
        style={{ width: '500px' }}
        footer={
          <div className="dialog-footer">
            <ModernButton
              variant="secondary"
              onClick={() => setShowRoomDialog(false)}
            >
              Cancel
            </ModernButton>
            <ModernButton
              variant="primary"
              onClick={handleSaveRoom}
            >
              Save
            </ModernButton>
          </div>
        }
      >
        <div className="room-dialog-content">
          <div className="dialog-field">
            <label htmlFor="roomName">Display Name</label>
            <InputText
              id="roomName"
              value={selectedRoom?.name || ''}
              onChange={(e) => setSelectedRoom(prev => ({ ...prev!, name: e.target.value }))}
              placeholder="e.g., Bathroom (40-70 sq ft | $225/sq ft)"
            />
          </div>
          <div className="dialog-field">
            <label htmlFor="roomShortName">Short Name</label>
            <InputText
              id="roomShortName"
              value={selectedRoom?.shortName || ''}
              onChange={(e) => setSelectedRoom(prev => ({ ...prev!, shortName: e.target.value }))}
              placeholder="e.g., Bathroom"
            />
          </div>
          <div className="dialog-field">
            <label htmlFor="roomType">Type</label>
            <Dropdown
              id="roomType"
              value={selectedRoom?.type || ''}
              options={[
                { label: 'Bedroom', value: 'bed' },
                { label: 'Bathroom', value: 'bath' },
                { label: 'Room', value: 'room' },
                { label: 'Kitchen', value: 'kitchen' },
                { label: 'Garage', value: 'garage' },
                { label: 'Closet', value: 'closet' },
                { label: 'Utility', value: 'util' },
                { label: 'Laundry', value: 'laundry' }
              ]}
              onChange={(e) => setSelectedRoom(prev => ({ ...prev!, type: e.value }))}
              placeholder="Select type"
            />
          </div>
          <div className="dialog-grid">
            <div className="dialog-field">
              <label htmlFor="roomLength">Length (ft)</label>
              <InputNumber
                id="roomLength"
                value={selectedRoom?.length || 0}
                onValueChange={(e) => setSelectedRoom(prev => ({ ...prev!, length: e.value || 0 }))}
                min={1}
                max={100}
              />
            </div>
            <div className="dialog-field">
              <label htmlFor="roomWidth">Width (ft)</label>
              <InputNumber
                id="roomWidth"
                value={selectedRoom?.width || 0}
                onValueChange={(e) => setSelectedRoom(prev => ({ ...prev!, width: e.value || 0 }))}
                min={1}
                max={100}
              />
            </div>
          </div>
          <div className="dialog-grid">
            <div className="dialog-field">
              <label htmlFor="roomArea">Area (sq ft)</label>
              <InputNumber
                id="roomArea"
                value={selectedRoom?.area || 0}
                onValueChange={(e) => setSelectedRoom(prev => ({ ...prev!, area: e.value || 0 }))}
                min={1}
              />
            </div>
            <div className="dialog-field">
              <label htmlFor="roomCostSqFt">Cost per sq ft</label>
              <InputNumber
                id="roomCostSqFt"
                value={selectedRoom?.costSqFt || 0}
                onValueChange={(e) => setSelectedRoom(prev => ({ ...prev!, costSqFt: e.value || 0 }))}
                mode="currency"
                currency="USD"
              />
            </div>
          </div>
          <div className="dialog-field">
            <label htmlFor="roomTotalCost">Total Cost</label>
            <InputNumber
              id="roomTotalCost"
              value={selectedRoom?.cost || 0}
              onValueChange={(e) => setSelectedRoom(prev => ({ ...prev!, cost: e.value || 0 }))}
              mode="currency"
              currency="USD"
            />
          </div>
        </div>
      </Dialog>

      {/* Style Dialog */}
      <Dialog
        visible={showStyleDialog}
        onHide={() => setShowStyleDialog(false)}
        header={selectedStyle ? "Edit Home Style" : "Add Home Style"}
        style={{ width: '600px' }}
        footer={
          <div className="dialog-footer">
            <ModernButton
              variant="secondary"
              onClick={() => setShowStyleDialog(false)}
            >
              Cancel
            </ModernButton>
            <ModernButton
              variant="primary"
              onClick={handleSaveStyle}
            >
              Save
            </ModernButton>
          </div>
        }
      >
        <div className="style-dialog-content">
          <div className="dialog-field">
            <label htmlFor="styleName">Style Name</label>
            <InputText
              id="styleName"
              value={selectedStyle?.name || ''}
              onChange={(e) => setSelectedStyle(prev => ({ ...prev!, name: e.target.value }))}
              placeholder="e.g., Modern, Contemporary, Victorian"
            />
          </div>
          <div className="dialog-field">
            <label htmlFor="styleDescription">Description</label>
            <InputTextarea
              id="styleDescription"
              value={selectedStyle?.description || ''}
              onChange={(e) => setSelectedStyle(prev => ({ ...prev!, description: e.target.value }))}
              rows={10}
              placeholder="Enter a detailed description of this home style..."
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default SystemSettingsPage;
